import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "client";
import { useUtilities, axiosInstance, useSearch, printError } from "helpers";
import { t } from "i18next";
import { useEffect } from "react";
import { batch } from "react-redux";
import { staleDefaultChecklists } from "redux/reducers/templateLibrary/checklists";
import { staleDefaultContentRequest } from "redux/reducers/templateLibrary/contentRequests";
import {
  getAllTemplatePhases,
  addTemplatePhase,
  deleteTemplatePhase,
  updateTemplatePhase,
  getAllDefaultPhases,
  addDefaultPhase,
  deleteDefaultPhase,
  makeDefaultPhaseStale,
  makePhaseStale,
} from "redux/reducers/templateLibrary/phases";
import { staleTemplateLibraryTasks } from "redux/reducers/templateLibrary/tasks";

export async function getTemplatePhases(web) {
  return axiosInstance
    .post("showphaseslibrarytemplate", {
      api_key: web ? web : localStorage.getItem("apikey"),
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetTemplatePhases(web) {
  return useQuery(["templatePhases", web], () => getTemplatePhases(web));
}

export function usePhases(web = "") {
  const {
    dispatch,
    phases: { phases, loaded },
  } = useUtilities();
  const { data, isLoading, error, isRefetching } = useGetTemplatePhases(web);
  useEffect(() => {
    if (isLoading || loaded || isRefetching) return;
    if (data) {
      dispatch(getAllTemplatePhases(data.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, loaded, isRefetching]);

  return { data: phases, isLoading: isLoading || !loaded, error };
}

export async function addNewPhase(phase) {
  return axiosInstance
    .post("addphaseslibrarytemplate", {
      website_id: localStorage.getItem("apikey"),
      wpsl_tem: localStorage.getItem("apikey"),
      target_date: "",
      default_tem: "",
      status: [],
      ...phase,
      api_key: localStorage.getItem("apikey"),
    })
    .then((res) => res.data);
}

export function useAddNewPhase(redirect = "", updateDefaultState = false) {
  const { web = null } = useSearch();
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(addNewPhase, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        if (updateDefaultState) {
          dispatch(addDefaultPhase(data.data));
        } else {
          dispatch(addTemplatePhase(data.data));
        }
        notification({
          type: "success",
          message: t("siteSetting.phases.api.phaseAdded"),
        });
        if (redirect)
          navigate(`${redirect}${data.data.id}${web ? "?web=" + web : ""}`);
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: printError(error.response.data.message),
      });
    },
  });
}

export async function deletePhase(id) {
  return axiosInstance
    .post("deletelibrarytemplaphases", {
      api_key: localStorage.getItem("apikey"),
      id,
    })
    .then((res) => ({ ...res.data, id }));
}

export function useDeletePhase(redirect = "") {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(deletePhase, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteTemplatePhase(data.id));
        notification({
          type: "success",
          message: t("siteSetting.phases.api.phaseDeleted"),
        });
        if (redirect) navigate(redirect);
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: printError(error.response.data.message),
      });
    },
  });
}

export async function updatePhase(phase) {
  return axiosInstance
    .post("editphaseslibrarytemplate", {
      ...phase,
      api_key: localStorage.getItem("apikey"),
    })
    .then((res) => res.data);
}

export function useUpdatePhase() {
  const { dispatch, notification } = useUtilities();
  return useMutation(updatePhase, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(updateTemplatePhase(data.data));
        notification({
          type: "success",
          message: t("siteSetting.phases.api.phaseUpdated"),
        });
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: printError(error.response.data.message),
      });
    },
  });
}

export async function getDefaultPhase(web) {
  return axiosInstance
    .post("showdefaulttemplatephases", {
      api_key: localStorage.getItem("apikey"),
      website_id: web,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetDefaultPhase() {
  const { web = null } = useSearch();
  return useQuery(["defaultPhase", web], () => getDefaultPhase(web));
}

export function useDefaultPhases() {
  const { web = null } = useSearch();
  const { data, isLoading, error, isRefetching } = useGetDefaultPhase();
  const {
    dispatch,
    phases: {
      defaultSite: { phases, loaded, website_id },
    },
  } = useUtilities();

  useEffect(() => {
    if (data && (website_id !== web || !loaded) && !isRefetching) {
      dispatch(getAllDefaultPhases({ phases: data.data, website_id: web }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, loaded, web, website_id, isRefetching]);
  return {
    data: phases,
    isLoading: isLoading || web !== website_id,
    error,
  };
}

export async function addDefaultTemplatePhase(data) {
  return axiosInstance
    .post("adddefaulttemplatephases", {
      api_key: localStorage.getItem("apikey"),
      ...data,
    })
    .then((res) => res.data);
}

export function useAddDefaultPhase() {
  const { dispatch, notification } = useUtilities();
  return useMutation(addDefaultTemplatePhase, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addDefaultPhase(data.data));
        notification({
          type: "success",
          message: t("siteSetting.phases.api.phaseAddedToDefault"),
        });
        queryClient.invalidateQueries(["wpslTasksList"]);
        queryClient.invalidateQueries(["checklistDefaultTemplates"]);
        queryClient.invalidateQueries(["defaultContentRequest"]);
        batch(() => {
          dispatch(staleDefaultChecklists());
          dispatch(staleDefaultContentRequest());
          dispatch(staleTemplateLibraryTasks());
        });
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) =>
      notification({
        type: "error",
        message: printError(error.response.data.message),
      }),
  });
}

export async function deleteDefaultTemplatePhase(data) {
  return axiosInstance
    .post("defaultphasesremove", {
      api_key: localStorage.getItem("apikey"),
      ...data,
    })
    .then((res) => ({ ...res.data, ...data }));
}

export function useDeleteDefaultPhase() {
  const { dispatch, notification } = useUtilities();
  return useMutation(deleteDefaultTemplatePhase, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteDefaultPhase(data.id));
        notification({
          type: "success",
          message: t("siteSetting.phases.api.phaseRemovedToDefault"),
        });

        queryClient.invalidateQueries(["wpslTasksList"]);
        queryClient.invalidateQueries(["checklistDefaultTemplates"]);
        queryClient.invalidateQueries(["defaultContentRequest"]);
        batch(() => {
          dispatch(staleDefaultChecklists());
          dispatch(staleDefaultContentRequest());
          dispatch(staleTemplateLibraryTasks());
        });
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) =>
      notification({
        type: "error",
        message: printError(error.response.data.message),
      }),
  });
}

async function updatePhasesOrder(data) {
  return axiosInstance
    .post("phases/update/order", {
      api_key: localStorage.getItem("apikey"),
      data,
    })
    .then((res) => res.data);
}

export function useUpdatePhasesOrder() {
  const { dispatch, notification } = useUtilities();
  return useMutation(updatePhasesOrder, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        queryClient.invalidateQueries(["defaultPhase"]);
        dispatch(makeDefaultPhaseStale());
      }
    },
    onError: (error) =>
      notification({
        type: "error",
        message: error.message,
      }),
  });
}

export function invalidateTemplatePhases() {
  return (dispatch) => {
    dispatch(makePhaseStale());
    queryClient.invalidateQueries(["templatePhases"]);
  };
}
